import { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { formatCrmDateTime } from '../../kanban-board/formatters/crm-datetime';
import { LeadBlock } from './lead-block';
import { LeadStatusChip } from './lead-status-chip';
import { ELeadPaymentStatus, LeadPayment, LeadPaymentService, PROVIDER_NAME } from '../services/lead-payment.service';

const STATUS: Record<ELeadPaymentStatus, { key: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
    [ELeadPaymentStatus.Pending]: { key: 'cobranca.aguardando', color: 'warning' },
    [ELeadPaymentStatus.Paid]: { key: 'cobranca.pago', color: 'success' },
    [ELeadPaymentStatus.Cancelled]: { key: 'cobranca.cancelado', color: 'default' },
    [ELeadPaymentStatus.Refunded]: { key: 'cobranca.estornado', color: 'error' },
};

const money = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface Props {
    leadId: string;
    columns?: number;
    /// Sobe quando o cabeçalho gera uma cobrança.
    version?: number;
}

/// Links de pagamento do lead; sem cobrança o bloco some, para quem não usa Mercado Pago.
export const LeadPayments = ({ leadId, columns, version = 0 }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [payments, setPayments] = useState<LeadPayment[] | null>(null);

    const load = useCallback(() => {
        new LeadPaymentService()
            .list(leadId)
            .then((res) => setPayments(res.data.data ?? []))
            .catch(() => setPayments([]));
    }, [leadId]);

    useEffect(load, [load, version]);

    if (payments !== null && payments.length === 0) return null;

    const copy = (url: string) =>
        navigator.clipboard.writeText(url).then(() => showAlert(t('integracoes.copiado'), 'success', 1500));

    return (
        <LeadBlock
            title={t('cobranca.bloco')}
            count={payments ? String(payments.length) : undefined}
            columns={columns}
        >
            {payments === null ? (
                <Skeleton variant="rounded" height={48} />
            ) : (
                payments.map((payment) => (
                    <Box
                        key={payment.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            py: 0.9,
                            '& + &': { borderTop: `1px solid ${cc.divider}` },
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: cc.textPrimary }}>
                                {money(payment.paidAmount ?? payment.amount)} · {payment.description}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: 11.5, color: cc.textMuted }}>
                                {payment.paidAt
                                    ? t('cobranca.pagoEm', { data: formatCrmDateTime(payment.paidAt) })
                                    : formatCrmDateTime(payment.createdAt)}
                                {` · ${PROVIDER_NAME[payment.provider] ?? payment.provider}`}
                                {payment.createdByName ? ` · ${payment.createdByName}` : ''}
                            </Typography>
                        </Box>

                        <LeadStatusChip
                            label={t(STATUS[payment.status].key)}
                            color={STATUS[payment.status].color}
                        />

                        {payment.status === ELeadPaymentStatus.Pending && (
                            <Tooltip title={t('cobranca.copiarLink')}>
                                <IconButton
                                    size="small"
                                    onClick={() => copy(payment.checkoutUrl)}
                                    sx={{ color: cc.textMuted }}
                                >
                                    <ContentCopyIcon sx={{ fontSize: '17px !important' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                ))
            )}
        </LeadBlock>
    );
};
